import Event from '../../../classes/event';
import Focus from '../../../types/enums/focus';
import Main from '../../../types/enums/main';
import Rail from '../../../classes/rail';
import TranslationManager from '../../../assets/locale/translationsManager';
import autoCompleteRequest from '../../../api/search/autocompleteRequest';
import searchRequest from '../../../api/search/searchRequest';
import searchReelsRequest from '../../../api/search/searchReelsRequest';
import { AutoComplete } from '@shared/types/interfaces/search';
import { store } from '../../../store/store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useRef, useEffect, useState } from 'react';
import {
  focusSelector,
  pushFocus,
  setFocus,
} from '../../../store/slices/focus.slice';
import {
  routerSelector,
  setParams,
  updateParams,
} from '../../../store/slices/router.slice';
import FastEvent from '@shared/classes/fastEvent';

function useSearch() {
  const dispatch = useAppDispatch();

  // Selectors

  const { focus, stack } = useAppSelector(focusSelector);
  const { params } = useAppSelector(routerSelector);

  // States

  const [animation, setAnimation] = useState('search-content-load');
  const [asset, setAsset] = useState(
    (params?.current?.rails as Rail[])?.length
      ? ((params?.current?.rails as Rail[])[0]?.data[0] as Event)
      : undefined
  );
  const [input, setInput] = useState((params?.current?.input as string) || '');
  const [message, setMessage] = useState('');
  const [opacity, setOpacity] = useState(0);
  const [queries, setQueries] = useState<AutoComplete[]>(
    (params?.current?.queries as AutoComplete[]) || []
  );
  const [rails, setRails] = useState<Rail[]>(
    (params?.current?.rails as Rail[]) || []
  );
  const [selection, setSelection] = useState(
    (params?.current?.query as AutoComplete | undefined)?.title
  );
  const [totalResults, setTotalResults] = useState<number | null>(null);

  const [error, setError] = useState(false);

  // Refs

  const currentRail = useRef<number | undefined>(
    (params?.current?.verticalIndex as number) || 0
  );
  const inputRef = useRef<string>('');
  const queryRef = useRef<AutoComplete[]>(queries);
  const railsRef = useRef<Rail[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>();
  const searchRef = useRef(0);
  const searchingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const originalReels = useRef<Rail[]>(); // Rails from the Search Reels request, shown before performing a search
  inputRef.current = input;
  queryRef.current = queries;
  railsRef.current = rails;

  // Actions

  const clearAction = () => {
    // Clear input next to keyboard
    inputRef.current = '';
    setInput('');

    // Clear queries below keyboard
    queryRef.current = [];
    setQueries([]);

    // Clear rails
    currentRail.current = 0;
    railsRef.current = [];
    setRails(originalReels.current ? originalReels.current : []);

    // Clear asset
    setAsset(undefined);

    // Clear total results
    setTotalResults(null);

    // Clear message
    if (!searchingRef.current) {
      setMessage('');
    }
  };

  const outOfBoundsAction = (focus: Focus | number | string, key?: string) => {
    const { focus: focusSlice } = store.getState();
    if (
      key === 'DOWN' &&
      focusSlice.focus === Focus.SEARCH_KEYBOARD &&
      focus === Focus.SEARCH_KEYBOARD &&
      queryRef.current.length
    ) {
      setTimeout(() => {
        dispatch(setFocus(Focus.QUERY_LIST));
      });
    } else if (
      key === 'UP' &&
      focusSlice.focus === Focus.QUERY_LIST &&
      focus === Focus.QUERY_LIST
    ) {
      setTimeout(() => {
        dispatch(setFocus(Focus.SEARCH_KEYBOARD));
      });
    } else if (
      key === 'RIGHT' &&
      railsRef.current.length &&
      (focus === Focus.SEARCH_KEYBOARD || focus === Focus.QUERY_LIST)
    ) {
      setTimeout(() => {
        dispatch(
          setFocus([
            Focus.RAILS_LIST,
            parseInt(`${Focus.RAILS_SUB_LIST}${currentRail.current || 0}`),
          ])
        );
      });
    } else if (key === 'LEFT') {
      if (focus === Focus.QUERY_LIST || focus === Focus.SEARCH_KEYBOARD) {
        setTimeout(() => {
          dispatch(pushFocus(Focus.MENU));
        });
      } else if (focus !== Focus.RAILS_LIST) {
        setTimeout(() => {
          dispatch(setFocus(Focus.SEARCH_KEYBOARD));
        });
      }
    }
  };

  const searchAction = (query?: AutoComplete) => {
    // Search identifier
    searchRef.current += 1;
    const searchId = searchRef.current;

    // Reset data and display loading message
    clearInterval(intervalRef.current);
    setMessage(TranslationManager.get('COMPONENTS.SEARCH.SEARCHING'));
    searchingRef.current = true;

    // Set query
    const queryId = query?._id || '';
    const queryTitle = query?.title || inputRef.current || '';

    // Dynamic loading message feedback
    let dots = '';
    intervalRef.current = setInterval(() => {
      if (dots.length > 2) {
        dots = '';
      } else {
        dots += '.';
      }
      setMessage(TranslationManager.get('COMPONENTS.SEARCH.SEARCHING') + dots);
    }, 500);

    // Set message timeout
    const replaceMessage = (message: string) => {
      searchingRef.current = false;
      if (searchId === searchRef.current) {
        clearInterval(intervalRef.current);
        setMessage(message ? TranslationManager.get(message) : '');
      }
    };

    // Request content
    setInput(queryTitle);
    setSelection(undefined);
    searchRequest(queryTitle?.trim(), queryId)
      .then((rails: Rail[]) => {
        setTimeout(() => {
          currentRail.current = 0;
          dispatch(
            setParams({
              input: queryTitle,
              query: query,
              queries: queryRef.current,
              rails,
            })
          );
          if (rails.length > 0) {
            setError(false);
            setRails(rails);
            dispatch(
              setFocus([
                Focus.RAILS_LIST,
                parseInt(`${Focus.RAILS_SUB_LIST}${0}`),
              ])
            );
            setTimeout(() => {
              setAsset(
                rails?.length && rails[0]?.data?.length
                  ? (rails[0]?.data[0] as Event)
                  : undefined
              );
            }, 500);
          } else {
            setError(true);
          }
          replaceMessage(rails?.length ? '' : 'COMPONENTS.SEARCH.NO_RESULTS');
        }, 2000);
      })
      .catch(() => {
        setError(true);
        replaceMessage('COMPONENTS.SEARCH.ERROR');
      })
      .finally(() => {
        setSelection(queryTitle);
        setTotalResults(
          railsRef.current?.length
            ? railsRef.current?.reduce((prev, current) => {
                return (prev += current.data.length || 0);
              }, 0)
            : null
        );
      });
  };

  // Event handlers

  const onChangeElement = (
    type: Main,
    hIndex?: number | null,
    vIndex?: number
  ) => {
    currentRail.current = vIndex;
    if (vIndex || vIndex === 0) {
      dispatch(
        updateParams({
          horizontalIndex: hIndex,
          verticalIndex: vIndex,
        })
      );
    } else {
      dispatch(
        updateParams({
          horizontalIndex: hIndex,
        })
      );
    }
  };

  const onChangeInput = (char: string) => {
    clearTimeout(timeoutRef.current);
    setInput((prev) => {
      const newValue = prev + char;
      if (newValue.length > 2) {
        timeoutRef.current = setTimeout(() => {
          autoCompleteRequest(newValue || '').then((queries) => {
            setQueries(queries);
          });
        }, 200);
      }
      return newValue;
    });
  };

  const onDeleteInput = (pressed = false) => {
    setInput((prev) => (!pressed ? prev.substring(0, prev.length - 1) : ''));
  };

  const onMouseEnter = () => {
    dispatch(setFocus(Focus.SEARCH_KEYBOARD));
  };

  // Effect hooks

  useEffect(() => {
    var { router } = store.getState();
    if (!router?.params?.current) {
      searchReelsRequest().then((rails) => {
        originalReels.current = rails;
        setRails(rails);
        dispatch(
          setParams({
            queries: queryRef.current,
            rails,
          })
        );
      });
    }
  }, []);

  useEffect(() => {
    dispatch(
      setFocus(
        (params?.current?.previousFocus as Focus | Focus[]) ||
          Focus.SEARCH_KEYBOARD
      )
    );
    setTimeout(() => {
      if (params?.current?.asset) {
        setAsset(params?.current?.asset as FastEvent);
      }
      setOpacity(1);
    }, 100);
    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setAnimation((prev) =>
      focus === Focus.MENU ||
      (focus === Focus.LOGIN && stack.includes(Focus.MENU))
        ? 'search-content-out'
        : prev === 'search-content-load'
        ? prev
        : 'search-content-in'
    );
    if (focus !== -1 && focus !== Focus.FLOATING_MENU) {
      dispatch(
        updateParams({
          previousFocus: focus,
        })
      );
    }
  }, [focus]);

  useEffect(() => {
    dispatch(
      updateParams({
        asset,
      })
    );
  }, [asset]);

  return {
    animation,
    asset,
    error,
    input,
    message,
    opacity,
    queries,
    rails,
    searching: searchingRef.current,
    selection,
    totalResults,
    clearAction,
    searchAction,
    setAsset,
    onChangeElement,
    onChangeInput,
    onDeleteInput,
    onMouseEnter,
    outOfBoundsAction,
  };
}

export default useSearch;
